import React, { useState, useEffect, useCallback } from 'react';
import baseapi from '../../../services/baseapi';
import {
  Activity, Server, AlertTriangle, Layers, ChevronLeft, ChevronRight,
  Cpu, Network, ArrowDown, ArrowUp,
  Boxes, RefreshCw, Database, Play, Clock
} from 'lucide-react';
import Chart from 'react-apexcharts';

interface OverviewTabProps {
  agentId: string;
  clientId: string;
}

const fmtBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

interface ResourceGaugeProps {
  percentage: number;
  label: string;
  valueText: string;
  subText?: string;
  color: string;
  gradientColor: string;
}

const ResourceGauge: React.FC<ResourceGaugeProps> = ({
  percentage,
  label,
  valueText,
  subText,
  color,
  gradientColor
}) => {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  const chartOptions = {
    chart: {
      type: 'radialBar' as const,
      sparkline: {
        enabled: true
      },
      animations: {
        enabled: true,
        dynamicAnimation: {
          speed: 350
        }
      }
    },
    plotOptions: {
      radialBar: {
        hollow: {
          size: '65%'
        },
        track: {
          background: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
          strokeWidth: '100%'
        },
        dataLabels: {
          name: {
            show: true,
            fontSize: '11px',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: isDark ? '#94a3b8' : '#64748b',
            offsetY: 1
          },
          value: {
            show: true,
            fontSize: '13px',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: isDark ? '#f8fafc' : '#1e293b',
            offsetY: 1,
            formatter: (val: number) => `${val.toFixed(0)}%`
          }
        }
      }
    },
    grid: {
      padding: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }
    },
    fill: {
      type: 'gradient' as const,
      gradient: {
        shade: isDark ? 'dark' : 'light',
        type: 'horizontal' as const,
        shadeIntensity: 0.5,
        gradientToColors: [gradientColor],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    },
    colors: [color],
    stroke: {
      lineCap: 'round' as const
    },
    labels: [label]
  };

  return (
    <div className="flex flex-col items-center justify-center text-center w-full">
      <div className="w-full flex items-center justify-center relative h-[105px]">
        <Chart
          options={chartOptions}
          series={[percentage]}
          type="radialBar"
          width="100%"
          height="125"
        />
      </div>
      <div className="min-h-[28px] flex flex-col justify-start mt-[2px] relative z-10">
        <span className="text-[11px] font-mono font-bold text-gray-800 dark:text-gray-200 leading-tight">{valueText}</span>
        {subText && <span className="text-[9px] font-sans text-gray-500 dark:text-gray-400 mt-0.5 leading-none">{subText}</span>}
      </div>
    </div>
  );
};

interface PodHealthGaugeProps {
  percentage: number;
  label: string;
  valueText: string;
  subText: string;
}

const PodHealthGauge: React.FC<PodHealthGaugeProps> = ({
  percentage,
  label,
  valueText,
  subText
}) => {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  const chartOptions = {
    chart: {
      type: 'radialBar' as const,
      sparkline: {
        enabled: true
      },
      animations: {
        enabled: true,
        dynamicAnimation: {
          speed: 350
        }
      }
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: {
          size: '60%'
        },
        track: {
          background: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
          strokeWidth: '95%'
        },
        ticks: {
          show: true,
          major: {
            count: 6,
            length: 8,
            width: 1.5,
            color: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.2)',
            placement: 'outside'
          },
          minor: {
            count: 2,
            length: 4,
            width: 1,
            color: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
            placement: 'outside'
          }
        },
        dataLabels: {
          name: {
            show: true,
            fontSize: '9px',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: isDark ? '#94a3b8' : '#64748b',
            offsetY: -15
          },
          value: {
            show: true,
            fontSize: '13px',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: isDark ? '#f8fafc' : '#1e293b',
            offsetY: -2,
            formatter: (val: number) => `${val.toFixed(0)}%`
          }
        }
      } as any
    },
    grid: {
      padding: {
        top: -10,
        bottom: 0,
        left: 0,
        right: 0
      }
    },
    fill: {
      type: 'gradient' as const,
      gradient: {
        shade: isDark ? 'dark' : 'light',
        type: 'horizontal' as const,
        shadeIntensity: 0.5,
        gradientToColors: ['#059669'],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    },
    colors: ['#10b981'],
    stroke: {
      lineCap: 'round' as const
    },
    labels: [label]
  };

  return (
    <div className="flex flex-col items-center justify-center text-center w-full">
      <div className="w-full flex items-center justify-center">
        <Chart
          options={chartOptions}
          series={[percentage]}
          type="radialBar"
          width="100%"
          height="110"
        />
      </div>
      <div className="min-h-[28px] flex flex-col justify-start mt-[-10px] relative z-10">
        <span className="text-[11px] font-mono font-bold text-gray-800 dark:text-gray-200 leading-tight">{valueText}</span>
        {subText && <span className="text-[9px] font-sans text-gray-500 dark:text-gray-400 mt-0.5 leading-none">{subText}</span>}
      </div>
    </div>
  );
};

interface ControllerCardProps {
  name: string;
  healthy: number;
  total: number;
  failed?: number;
  suspended?: number;
  icon: React.ReactNode;
  showTotalOnly?: boolean;
}

const ControllerCard: React.FC<ControllerCardProps> = ({
  name,
  healthy,
  total,
  failed,
  suspended,
  icon,
  showTotalOnly = false
}) => {
  const isUnhealthy = !showTotalOnly && (healthy < total || (failed && failed > 0));
  const isSuspended = !showTotalOnly && (suspended != null && suspended > 0);
  const isEmpty = total === 0;

  let stateColor: 'emerald' | 'red' | 'amber' | 'gray' | 'purple' = 'emerald';
  if (isEmpty) {
    stateColor = 'gray';
  } else if (isUnhealthy) {
    stateColor = 'red';
  } else if (isSuspended) {
    stateColor = 'amber';
  } else if (showTotalOnly) {
    stateColor = 'purple';
  }

  let statusText = 'Healthy';
  if (showTotalOnly) {
    statusText = '';
  } else if (isEmpty) {
    statusText = 'Inactive';
  } else if (isUnhealthy) {
    statusText = failed && failed > 0 ? `${failed} Failed` : 'Degraded';
  } else if (isSuspended) {
    statusText = `${suspended} Suspended`;
  }

  const colorMap = {
    emerald: {
      border: 'border-emerald-500/20 hover:border-emerald-500/40 hover:shadow-md dark:hover:shadow-[0_4px_16px_rgba(16,185,129,0.15)]',
      iconBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
      progress: 'bg-emerald-500',
      text: 'text-emerald-600 dark:text-emerald-400'
    },
    red: {
      border: 'border-red-500/20 hover:border-red-500/40 hover:shadow-md dark:hover:shadow-[0_4px_16px_rgba(239,68,68,0.15)]',
      iconBg: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
      progress: 'bg-red-500',
      text: 'text-red-600 dark:text-red-400'
    },
    amber: {
      border: 'border-amber-500/20 hover:border-amber-500/40 hover:shadow-md dark:hover:shadow-[0_4px_16px_rgba(245,158,11,0.15)]',
      iconBg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
      progress: 'bg-amber-500',
      text: 'text-amber-600 dark:text-amber-400'
    },
    gray: {
      border: 'border-gray-200 dark:border-white/5 hover:border-gray-300 dark:hover:border-white/10 hover:shadow-sm',
      iconBg: 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-white/5',
      progress: 'bg-gray-300 dark:bg-gray-700',
      text: 'text-gray-500 dark:text-gray-400'
    },
    purple: {
      border: 'border-purple-500/20 hover:border-purple-500/40 hover:shadow-md',
      iconBg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
      progress: 'bg-purple-500',
      text: 'text-purple-600 dark:text-purple-400'
    }
  }[stateColor];

  const pct = total > 0 ? Math.min((healthy / total) * 100, 100) : 0;

  return (
    <div className={`bg-white dark:bg-gray-800/90 backdrop-blur-md border ${colorMap.border} rounded-xl p-3 flex flex-col justify-between transition-all duration-200 hover:-translate-y-0.5 shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)]`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex flex-col min-w-0">
          <span className="font-sans text-[10px] font-semibold text-gray-600 dark:text-gray-400 truncate uppercase tracking-wider">{name}</span>
          <span className="font-mono text-sm font-bold text-gray-900 dark:text-white mt-0.5">
            {showTotalOnly ? `${total}` : (total > 0 ? `${healthy}/${total}` : '0/0')}
          </span>
        </div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${colorMap.iconBg}`}>
          {icon}
        </div>
      </div>
      {showTotalOnly ? (
        <div className="flex items-center text-[9px] font-mono leading-none mt-4">
          <span className="text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Ready nodes: {healthy} </span>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="h-1 bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden w-full">
            <div
              className={`h-full rounded-full transition-all duration-500 ${colorMap.progress}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-[9px] font-mono leading-none">
            <span className={colorMap.text}>{statusText}</span>
            <span className="text-gray-500 dark:text-gray-400">{pct.toFixed(0)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

const OverviewTab: React.FC<OverviewTabProps> = ({ agentId, clientId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [alertsPage, setAlertsPage] = useState(1);
  const [podsPage, setPodsPage] = useState(1);
  const [nodesPage, setNodesPage] = useState(1);

  // Reset data and set loading to true when cluster credentials change
  useEffect(() => {
    setData(null);
    setLoading(true);
  }, [agentId, clientId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const baseParams: any = { agent_id: agentId, client_id: clientId };
      if (clusterName) baseParams.cluster_name = clusterName;

      const [snapRes, alertsRes] = await Promise.all([
        baseapi.get('/k8s_dashboard/snapshot', { params: baseParams }),
        baseapi.get('/k8s_dashboard/events', { params: { ...baseParams, event_type: 'Warning', limit: 200 } })
      ]);
      if (snapRes.data?.status && snapRes.data?.data) {
        setData(snapRes.data.data);
      }
      if (alertsRes.data?.data) {
        setAlerts(alertsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) return <div className="text-gray-600 dark:text-gray-300 p-4 font-mono text-sm text-center">Loading overview...</div>;
  if (!data) return <div className="text-gray-600 dark:text-gray-300 p-4 font-mono text-sm text-center">No overview data available.</div>;

  const ALERTS_PER_PAGE = 5;
  const totalAlertPages = Math.max(1, Math.ceil(alerts.length / ALERTS_PER_PAGE));
  const paginatedAlerts = alerts.slice((alertsPage - 1) * ALERTS_PER_PAGE, alertsPage * ALERTS_PER_PAGE);

  const topPodsAll = [...(data?.pods || [])].sort((a: any, b: any) => {
    const aCpu = a.usage?.cpu_m ?? a.cpu_m ?? a.cpu_used_m ?? 0;
    const bCpu = b.usage?.cpu_m ?? b.cpu_m ?? b.cpu_used_m ?? 0;
    return bCpu - aCpu;
  });
  const PODS_PER_PAGE = 10;
  const totalPodsPages = Math.max(1, Math.ceil(topPodsAll.length / PODS_PER_PAGE));
  const paginatedPods = topPodsAll.slice((podsPage - 1) * PODS_PER_PAGE, podsPage * PODS_PER_PAGE);

  const NODES_PER_PAGE = 6;
  const totalNodesPages = Math.max(1, Math.ceil((data?.nodes?.length || 0) / NODES_PER_PAGE));
  const paginatedNodes = (data?.nodes || []).slice((nodesPage - 1) * NODES_PER_PAGE, nodesPage * NODES_PER_PAGE);

  const renderPageNumbers = (currentPage: number, totalPages: number, setPage: (page: number) => void) => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages, 5);
    } else if (currentPage >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setPage(i)}
          className={`w-6 h-6 flex items-center justify-center rounded text-[11px] font-mono border transition-colors ${currentPage === i
            ? 'bg-orange-500/20 border-orange-500/50 text-orange-600 dark:text-orange-400 font-bold'
            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm'
            }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const podsTotal = data?.summary?.pods_total || 0;
  const podsHealthy = data?.summary?.pods_healthy || 0;
  const podsPct = podsTotal > 0 ? (podsHealthy / podsTotal) * 100 : 0;

  const cpuPct = data?.summary?.cpu_pct ?? 0;
  const cpuColors = { base: '#3b82f6', grad: '#1d4ed8' }; // Blue

  const memPct = data?.summary?.mem_pct ?? 0;
  const memColors = { base: '#10b981', grad: '#047857' }; // Green

  const fsPct = data?.summary?.fs_pct ?? 0;
  const fsColors = { base: '#f97316', grad: '#c2410c' };  // Orange

  return (
    <div className="space-y-4">
      {/* Cluster Status Header */}
      <div className="bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-white/5 rounded-xl p-3 px-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 shadow-sm transition-colors duration-200">
        <div className="flex items-center gap-2.5">
          <div className={`w-2.5 h-2.5 rounded-full ${data?.status === 'HEALTHY' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]' : 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]'}`} />
          <span className="font-mono text-xs text-gray-500 dark:text-gray-400">Cluster:</span>
          <span className="font-mono text-sm font-extrabold text-gray-900 dark:text-white">{data?.cluster_name || 'k8s-cluster'}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${data?.status === 'HEALTHY' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'}`}>
            {data?.status || 'UNKNOWN'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-gray-600 dark:text-gray-400">
          {data?.cluster_info?.platform && (
            <span>Platform: <strong className="text-gray-800 dark:text-gray-200">{data.cluster_info.platform}</strong></span>
          )}
          {data?.cluster_info?.git_version && (
            <span>K8s Version: <strong className="text-gray-800 dark:text-gray-200">{data.cluster_info.git_version}</strong></span>
          )}
          {data?.collected_at && (
            <span>Last Sync: <strong className="text-gray-800 dark:text-gray-200">{new Date(data.collected_at).toLocaleString()}</strong></span>
          )}
          <button
            onClick={fetchData}
            className="w-8 h-8 flex items-center justify-center rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-orange-500 hover:border-orange-500/50 hover:bg-orange-500/10 shadow-sm transition-all ml-1"
            title="Refresh overview"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Cluster Warning / Node Pressure Banners */}
      {data?.summary?.nodes_with_pressure > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 py-2 flex items-center gap-2 text-xs font-mono text-red-600 dark:text-red-400 animate-pulse">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="font-bold">{data.summary.nodes_with_pressure} Node(s) reporting resource pressure!</span>
        </div>
      )}

      {/* Workload Controllers Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3.5">
        <ControllerCard
          name="Nodes"
          healthy={data?.summary?.nodes_ready ?? 0}
          total={data?.summary?.nodes_total ?? 0}
          icon={<Server size={16} />}
          showTotalOnly={true}
        />
        <ControllerCard
          name="Pods"
          healthy={data?.summary?.pods_healthy ?? 0}
          total={data?.summary?.pods_total ?? 0}
          icon={<Boxes size={16} />}
        />
        <ControllerCard
          name="Deployments"
          healthy={data?.summary?.deployments_healthy ?? 0}
          total={data?.summary?.deployments_total ?? 0}
          icon={<Layers size={16} />}
        />
        <ControllerCard
          name="StatefulSets"
          healthy={data?.summary?.statefulsets_healthy ?? 0}
          total={data?.summary?.statefulsets_total ?? 0}
          icon={<Database size={16} />}
        />
        <ControllerCard
          name="DaemonSets"
          healthy={data?.summary?.daemonsets_healthy ?? 0}
          total={data?.summary?.daemonsets_total ?? 0}
          icon={<Boxes size={16} />}
        />
        <ControllerCard
          name="Jobs"
          healthy={data?.summary?.jobs_healthy ?? 0}
          total={data?.summary?.jobs_total ?? 0}
          failed={data?.summary?.jobs_failed}
          icon={<Play size={16} />}
        />
        <ControllerCard
          name="CronJobs"
          healthy={(data?.summary?.cronjobs_total || 0) - (data?.summary?.cronjobs_suspended || 0)}
          total={data?.summary?.cronjobs_total ?? 0}
          suspended={data?.summary?.cronjobs_suspended}
          icon={<Clock size={16} />}
        />
      </div>

      {/* Primary Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5">
        {/* Card 1: Resource Utilization */}
        <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-xl p-3.5 flex flex-col justify-between shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)] transition-colors duration-200">
          <h3 className="font-mono text-[10px] tracking-[1.5px] uppercase text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3 mt-0">
            <Cpu className="w-3.5 h-3.5 text-orange-500 stroke-[2px]" />
            Resource Utilisation
          </h3>
          <div className="grid grid-cols-3 gap-1 flex-1 items-center">
            <ResourceGauge
              percentage={cpuPct}
              label="CPU"
              valueText={data?.summary?.cpu_used_m != null ? `${Math.round(data.summary.cpu_used_m)}m` : '0m'}
              subText={`Alloc: ${data?.summary?.cpu_alloc_m ?? 0}m`}
              color={cpuColors.base}
              gradientColor={cpuColors.grad}
            />
            <ResourceGauge
              percentage={memPct}
              label="Mem"
              valueText={data?.summary?.mem_used_bytes != null ? fmtBytes(data.summary.mem_used_bytes) : '0 B'}
              subText={`Alloc: ${data?.summary?.mem_alloc_bytes != null ? fmtBytes(data.summary.mem_alloc_bytes) : '0 B'}`}
              color={memColors.base}
              gradientColor={memColors.grad}
            />
            <ResourceGauge
              percentage={fsPct}
              label="FS"
              valueText={data?.summary?.fs_used_bytes != null ? fmtBytes(data.summary.fs_used_bytes) : '0 B'}
              subText={`Cap: ${data?.summary?.fs_capacity_bytes != null ? fmtBytes(data.summary.fs_capacity_bytes) : '0 B'}`}
              color={fsColors.base}
              gradientColor={fsColors.grad}
            />
          </div>
        </div>

        {/* Card 2: Pod Health & Performance */}
        <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-xl p-3.5 flex flex-col justify-between shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)] transition-colors duration-200">
          <h3 className="font-mono text-[10px] tracking-[1.5px] uppercase text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3 mt-0">
            <Boxes className="w-3.5 h-3.5 text-orange-500 stroke-[2px]" />
            Pod Health & Performance
          </h3>
          <div className="flex items-center justify-between flex-1 gap-2">
            <div className="shrink-0 pl-1 w-[90px]">
              <PodHealthGauge
                percentage={podsPct}
                label="Pods"
                valueText={`${podsHealthy}/${podsTotal}`}
                subText="Healthy"
              />
            </div>
            <div className="flex-1 flex flex-col justify-center gap-1.5 pl-3 border-l border-gray-200 dark:border-white/5">
              <div className="flex justify-between items-center text-[11px]">
                <span className="text-gray-600 dark:text-gray-400">Failed / Pending:</span>
                <span className="font-mono font-bold flex gap-1">
                  <span className={(data?.summary?.pods_failed || 0) > 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-300'}>{data?.summary?.pods_failed ?? 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className={(data?.summary?.pods_pending || 0) > 0 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-gray-300'}>{data?.summary?.pods_pending ?? 0}</span>
                </span>
              </div>
              <div className="flex justify-between items-center text-[11px]">
                <span className="text-gray-600 dark:text-gray-400">Problem Pods:</span>
                <span className={`font-mono font-bold ${(data?.summary?.problem_pods_count || 0) > 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-300'}`}>
                  {data?.summary?.problem_pods_count ?? 0}
                </span>
              </div>
              <div className="flex justify-between items-center text-[11px]">
                <span className="text-gray-600 dark:text-gray-400">Total Restarts:</span>
                <span className={`font-mono font-bold ${(data?.summary?.total_restarts || 0) > 0 ? 'text-amber-500 dark:text-amber-400 font-extrabold animate-pulse' : 'text-gray-400 dark:text-gray-300'}`}>
                  {data?.summary?.total_restarts ?? 0}
                </span>
              </div>
              <div className="flex justify-between items-center text-[10px] text-gray-500 dark:text-gray-400 italic leading-none">
                <span>Pods w/ restarts:</span>
                <span className="font-mono">{data?.summary?.pods_with_restarts ?? 0} pods</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 4: Network & Storage */}
        <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-xl p-3.5 flex flex-col justify-between shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)] transition-colors duration-200">
          <h3 className="font-mono text-[10px] tracking-[1.5px] uppercase text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2 mt-0">
            <Network className="w-3.5 h-3.5 text-orange-500 stroke-[2px]" />
            Network & Storage
          </h3>
          <div className="flex flex-col flex-1 justify-between gap-2">
            <div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 font-mono uppercase tracking-wider mb-1 flex items-center gap-1 justify-between">
                <span>Network Activity</span>
                <span className="text-gray-800 dark:text-gray-200 font-bold">{data?.summary?.network_total_bytes != null ? fmtBytes(data.summary.network_total_bytes) : '—'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1 flex items-center gap-1.5">
                  <ArrowDown className="w-3 h-3 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[8px] text-gray-500 dark:text-gray-400 font-sans uppercase leading-none">Rx</span>
                    <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-mono font-bold leading-tight mt-0.5 truncate">
                      {data?.summary?.network_rx_bytes != null ? fmtBytes(data.summary.network_rx_bytes) : '0 B'}
                    </span>
                  </div>
                </div>
                <div className="bg-blue-500/5 border border-blue-500/20 rounded px-2 py-1 flex items-center gap-1.5">
                  <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400 shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[8px] text-gray-500 dark:text-gray-400 font-sans uppercase leading-none">Tx</span>
                    <span className="text-[11px] text-blue-600 dark:text-blue-400 font-mono font-bold leading-tight mt-0.5 truncate">
                      {data?.summary?.network_tx_bytes != null ? fmtBytes(data.summary.network_tx_bytes) : '0 B'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center text-[10px] text-gray-600 dark:text-gray-400 font-mono uppercase tracking-wider mb-1">
                <span>PVC Binding</span>
                <span className="text-gray-800 dark:text-gray-200 font-bold">{data?.summary?.pvcs_total ?? 0} PVCs</span>
              </div>
              <div className="flex h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden shadow-inner">
                <div
                  className="h-full bg-emerald-500/80"
                  style={{ width: `${data?.summary?.pvcs_total ? ((data?.summary?.pvcs_bound || 0) / data?.summary?.pvcs_total) * 100 : 0}%` }}
                  title={`Bound: ${data?.summary?.pvcs_bound ?? 0}`}
                />
                <div
                  className="h-full bg-amber-500/80"
                  style={{ width: `${data?.summary?.pvcs_total ? ((data?.summary?.pvcs_pending || 0) / data?.summary?.pvcs_total) * 100 : 0}%` }}
                  title={`Pending: ${data?.summary?.pvcs_pending ?? 0}`}
                />
              </div>
              <div className="flex justify-between text-[9px] text-gray-500 dark:text-gray-400 mt-1 font-mono leading-none">
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Bound: {data?.summary?.pvcs_bound ?? 0}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Pending: {data?.summary?.pvcs_pending ?? 0}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center pt-1.5 border-t border-gray-200 dark:border-white/5 text-[9px] font-mono text-gray-600 dark:text-gray-400 leading-none">
              <span>Namespaces: <strong className="text-gray-800 dark:text-gray-200">{data?.summary?.namespaces_total ?? 0}</strong></span>
              <span>Services: <strong className="text-gray-800 dark:text-gray-200">{data?.summary?.services_total ?? 0}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-3">
        {/* Nodes Panel */}
        <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-xl mb-3 overflow-hidden shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)] flex flex-col transition-colors duration-200">
          <h3 className="font-mono text-[11px] tracking-[1.5px] uppercase text-gray-700 dark:text-gray-200 px-4 py-3 border-b border-gray-200 dark:border-white/5 bg-gray-50/80 dark:bg-black/15 flex items-center gap-2.5 m-0 shrink-0">
            <Server className="w-[15px] h-[15px] text-orange-500 shrink-0 stroke-[2.5px] drop-shadow-[0_2px_4px_rgba(255,122,24,0.4)]" />
            Nodes — live utilisation
          </h3>
          <div className="flex-1 overflow-y-auto custom-scroll">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 p-3">
              {paginatedNodes.length > 0 ? (
                paginatedNodes.map((node: any, idx: number) => {
                  const isDown = !node.ready;
                  const cpuPct = node.usage?.cpu_pct ?? node.cpu_pct;
                  const memPct = node.usage?.mem_pct ?? node.mem_pct;
                  return (
                    <div key={idx} className={`bg-gray-50/80 dark:bg-gray-800/90 backdrop-blur-sm border ${isDown ? 'border-red-500/40 bg-gradient-to-br from-red-50 to-red-100/50 dark:from-gray-900 dark:to-red-500/5' : 'border-gray-200 dark:border-gray-700'} rounded-xl p-3 transition-all duration-200 shadow-sm hover:border-gray-300 dark:hover:border-white/15 hover:-translate-y-0.5 hover:shadow-md hover:bg-white dark:hover:bg-gray-700`}>
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="font-mono font-extrabold text-[12px] text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis flex items-center gap-2">
                          <span>{node.name || node.node_name}</span>
                          {node.roles && node.roles.length > 0 && (
                            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 font-bold">
                              {node.roles[0]}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 mb-2.5">
                        <div className="font-mono text-[11px] text-gray-600 dark:text-gray-300 flex justify-between items-center">
                          <span>IP: {node.internal_ip}</span>
                          {node.arch && <span className="text-gray-500 dark:text-gray-400 text-[10px]">{node.arch}</span>}
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                          {node.kubelet && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-gray-200/60 dark:bg-white/5 text-gray-700 dark:text-gray-300 border border-gray-300/50 dark:border-white/5">
                              {node.kubelet}
                            </span>
                          )}
                          {node.os_image && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-gray-200/60 dark:bg-white/5 text-gray-700 dark:text-gray-300 border border-gray-300/50 dark:border-white/5">
                              {node.os_image}
                            </span>
                          )}
                          {node.instance_type && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-gray-200/60 dark:bg-white/5 text-gray-700 dark:text-gray-300 border border-gray-300/50 dark:border-white/5">
                              {node.instance_type}
                            </span>
                          )}
                        </div>
                        {node.pressures && node.pressures.length > 0 && (
                          <div className="flex gap-1 mt-0.5 flex-wrap">
                            {node.pressures.map((press: string, i: number) => (
                              <span key={i} className="text-[8px] font-mono font-bold bg-red-500/20 text-red-600 dark:text-red-400 px-1 py-0.5 rounded uppercase border border-red-500/30">
                                {press} Pressure
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* CPU & Memory Bars */}
                      <div className="grid grid-cols-2 gap-3 mt-1">
                        <div>
                          <div className="flex justify-between font-mono text-[10px] text-gray-600 dark:text-gray-400 mb-1">
                            <span>CPU</span>
                            <span>{cpuPct != null ? `${cpuPct.toFixed(1)}%` : '—'}</span>
                          </div>
                          <div className="h-1 bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden w-full shadow-inner">
                            <div
                              className="h-full rounded-full transition-all duration-600 ease-[cubic-bezier(0.25,0.8,0.25,1)]"
                              style={{
                                width: `${Math.min(cpuPct || 0, 100)}%`,
                                background: 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                              }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between font-mono text-[10px] text-gray-600 dark:text-gray-400 mb-1">
                            <span>Mem</span>
                            <span>{memPct != null ? `${memPct.toFixed(1)}%` : '—'}</span>
                          </div>
                          <div className="h-1 bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden w-full shadow-inner">
                            <div
                              className="h-full rounded-full transition-all duration-600 ease-[cubic-bezier(0.25,0.8,0.25,1)]"
                              style={{
                                width: `${Math.min(memPct || 0, 100)}%`,
                                background: 'linear-gradient(90deg, #10b981, #34d399)'
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-8 px-6 text-center text-gray-500 dark:text-gray-400 font-sans text-[13px] bg-gray-50 dark:bg-black/10 rounded-lg mx-3 my-3 border border-dashed border-gray-200 dark:border-white/10 col-span-1 sm:col-span-2">
                  No nodes found
                </div>
              )}
            </div>
          </div>

          {/* Nodes Pagination Controls */}
          <div className="flex justify-between items-center px-4 py-2 border-t border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-gray-900/50 mt-auto shrink-0">
            <span className="text-[11px] text-gray-600 dark:text-gray-400 font-mono">Page {nodesPage} of {totalNodesPages}</span>
            <div className="flex gap-1.5 items-center">
              <button
                onClick={() => setNodesPage(p => Math.max(1, p - 1))}
                disabled={nodesPage === 1}
                className="w-6 h-6 flex items-center justify-center rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronLeft size={14} />
              </button>
              {renderPageNumbers(nodesPage, totalNodesPages, setNodesPage)}
              <button
                onClick={() => setNodesPage(p => Math.min(totalNodesPages, p + 1))}
                disabled={nodesPage === totalNodesPages}
                className="w-6 h-6 flex items-center justify-center rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-xl mb-3 overflow-hidden shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)] flex flex-col transition-colors duration-200">
          <h3 className="font-mono text-[11px] tracking-[1.5px] uppercase text-gray-700 dark:text-gray-200 px-4 py-3 border-b border-gray-200 dark:border-white/5 bg-gray-50/80 dark:bg-black/15 flex items-center gap-2.5 m-0">
            <AlertTriangle className="w-[15px] h-[15px] text-orange-500 shrink-0 stroke-[2.5px] drop-shadow-[0_2px_4px_rgba(255,122,24,0.4)]" />
            Active alerts
          </h3>
          <div className="max-h-[380px] overflow-y-auto custom-scroll flex-1 flex flex-col">
            <div className="flex-1">
              {paginatedAlerts.length > 0 ? (
                paginatedAlerts.map((alert: any, idx: number) => (
                  <div key={idx} className="flex gap-3 px-4 py-2.5 my-1.5 mx-3 border border-gray-200 dark:border-white/5 rounded-lg bg-gray-50/80 dark:bg-white/5 font-mono text-xs items-center transition-all duration-200 hover:bg-gray-100/80 dark:hover:bg-white/10 hover:border-gray-300 dark:hover:border-white/10 hover:translate-x-0.5">
                    <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 shadow-[0_0_8px_rgba(245,158,11,0.6)]"></div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-900 dark:text-gray-100 font-bold truncate">{alert.object || alert.involved_object || alert.resource}</span>
                        {alert.reason && (
                          <span className="text-[9px] px-1 py-0.2 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 font-bold uppercase">
                            {alert.reason}
                          </span>
                        )}
                        {alert.count && alert.count > 1 && (
                          <span className="text-[9px] px-1.5 py-0.2 rounded-full bg-gray-200/80 dark:bg-white/5 text-gray-700 dark:text-gray-300 font-bold">
                            x{alert.count}
                          </span>
                        )}
                      </div>
                      <span className="text-gray-600 dark:text-gray-300 font-sans text-[12px] line-clamp-2 mt-0.5">{alert.message}</span>
                    </div>
                    {alert.event_time && (
                      <span className="text-[9px] text-gray-400 dark:text-gray-400 whitespace-nowrap hidden sm:block">
                        {new Date(alert.event_time).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <div className="py-8 px-6 text-center text-gray-500 dark:text-gray-400 font-sans text-[13px] bg-gray-50 dark:bg-black/10 rounded-lg mx-3 my-3 border border-dashed border-gray-200 dark:border-white/10">
                  No active alerts
                </div>
              )}
            </div>

            {/* Alerts Pagination Controls */}
            <div className="flex justify-between items-center px-4 py-2 border-t border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-gray-900 sticky bottom-0 z-10 mt-auto">
              <span className="text-[11px] text-gray-600 dark:text-gray-400 font-mono">Page {alertsPage} of {totalAlertPages}</span>
              <div className="flex gap-1.5 items-center">
                <button
                  onClick={() => setAlertsPage(p => Math.max(1, p - 1))}
                  disabled={alertsPage === 1}
                  className="w-6 h-6 flex items-center justify-center rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  <ChevronLeft size={14} />
                </button>
                {renderPageNumbers(alertsPage, totalAlertPages, setAlertsPage)}
                <button
                  onClick={() => setAlertsPage(p => Math.min(totalAlertPages, p + 1))}
                  disabled={alertsPage === totalAlertPages}
                  className="w-6 h-6 flex items-center justify-center rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Consumers Panel */}
      <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-xl mb-3 overflow-hidden shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)] transition-colors duration-200">
        <h3 className="font-mono text-[11px] tracking-[1.5px] uppercase text-gray-700 dark:text-gray-200 px-4 py-3 border-b border-gray-200 dark:border-white/5 bg-gray-50/80 dark:bg-black/15 flex items-center gap-2.5 m-0">
          <Activity className="w-[15px] h-[15px] text-orange-500 shrink-0 stroke-[2.5px] drop-shadow-[0_2px_4px_rgba(255,122,24,0.4)]" />
          Top consumers — live pod resource usage
        </h3>
        <div className="max-h-[380px] overflow-y-auto custom-scroll">
          <table className="w-full text-left text-xs border-collapse table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10 transition-colors duration-200">
              <tr className="border-b border-gray-200 dark:border-gray-700/50">
                <th className="w-[28%] px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest text-left">
                  Pod
                </th>

                <th className="w-[15%] px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest text-left">
                  Namespace
                </th>

                <th className="w-[10%] px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest text-left">
                  Node
                </th>

                <th className="w-[20%] px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest text-left">
                  CPU (live)
                </th>

                <th className="w-[20%] px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest text-left">
                  Memory (live)
                </th>

                <th className="w-[7%] px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest text-left">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedPods.map((pod: any, idx: number) => {
                const cpu_used = pod.usage?.cpu_m ?? pod.cpu_m ?? pod.cpu_used_m;
                const cpu_lim = pod.usage?.cpu_lim_m ?? pod.cpu_lim_m ?? pod.cpu_limit_m;
                const cpu_pct = pod.cpu_pct ?? (cpu_used && cpu_lim ? (cpu_used / cpu_lim) * 100 : 0);

                const mem_used = pod.usage?.mem_bytes ?? pod.mem_bytes ?? pod.mem_used_bytes;
                const mem_lim = pod.usage?.mem_lim_bytes ?? pod.mem_lim_bytes ?? pod.mem_limit_bytes;
                const mem_pct = pod.mem_pct ?? (mem_used && mem_lim ? (mem_used / mem_lim) * 100 : 0);

                return (
                  <tr key={idx} className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                    <td className="px-3 py-1 text-left font-medium text-gray-900 dark:text-gray-100">{pod.pod_name || pod.name}</td>
                    <td className="px-1 py-1 text-left font-mono text-gray-600 dark:text-gray-300 text-[11px]">{pod.namespace}</td>
                    <td className="px-1 py-1 text-left font-mono text-gray-600 dark:text-gray-300 text-[11px]">{pod.node_name || pod.node}</td>
                    <td className="px-2 py-1 align-middle whitespace-nowrap">
                      <div className="flex items-start gap-2">
                        <div className="w-28 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden relative">
                          <div className="absolute top-0 bottom-0 left-0 bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${Math.min(cpu_pct || 0, 100)}%` }} />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300 font-mono text-[11px] w-12 text-left">{cpu_used != null ? `${cpu_used}m` : '—'}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1 align-middle whitespace-nowrap">
                      <div className="flex items-start gap-2">
                        <div className="w-28 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden relative">
                          <div className="absolute top-0 bottom-0 left-0 bg-indigo-600 rounded-full transition-all duration-500" style={{ width: `${Math.min(mem_pct || 0, 100)}%` }} />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300 font-mono text-[11px] w-12 text-left">{mem_used != null ? fmtBytes(mem_used) : '—'}</span>
                      </div>
                    </td>
                    <td className="px-1 py-1 text-left font-mono">
                      <span className="text-[9px] px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400 whitespace-nowrap font-medium">
                        {pod.status || pod.phase}
                      </span>
                    </td>
                  </tr>
                )
              })}
              {paginatedPods.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 px-6 text-center text-gray-500 dark:text-gray-400 font-sans text-[13px] bg-gray-50 dark:bg-black/10 border-b border-gray-200 dark:border-white/5">
                    No pods found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Table Pagination Controls */}
          <div className="flex justify-between items-center px-4 py-2 border-t border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-gray-900 sticky bottom-0 z-10">
            <span className="text-[11px] text-gray-600 dark:text-gray-400 font-mono">Page {podsPage} of {totalPodsPages}</span>
            <div className="flex gap-1.5 items-center">
              <button
                onClick={() => setPodsPage(p => Math.max(1, p - 1))}
                disabled={podsPage === 1}
                className="w-6 h-6 flex items-center justify-center rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronLeft size={14} />
              </button>
              {renderPageNumbers(podsPage, totalPodsPages, setPodsPage)}
              <button
                onClick={() => setPodsPage(p => Math.min(totalPodsPages, p + 1))}
                disabled={podsPage === totalPodsPages}
                className="w-6 h-6 flex items-center justify-center rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
